import { useState, useEffect, useCallback } from 'react';
import { Bell, CheckCircle2, AlertCircle, Info, X, Check, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationCenter() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API}/notifications?limit=20`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: 'PUT',
        credentials: 'include'
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning': return <AlertCircle className="w-4 h-4 text-amber-500" />;
      default: return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatTime = (isoDate) => {
    const date = new Date(isoDate);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" data-testid="notification-center">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-[#72A4BF]/8 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-[#446E87]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Notification Panel */}
          <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white border border-[#E2E8ED] rounded-xl shadow-xl z-50 overflow-hidden"
               data-testid="notification-panel">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8ED] bg-[#F7FAFB]">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm text-[#0D1830]">Notifications</h3>
                {unreadCount > 0 && (
                  <Badge className="bg-[#72A4BF]/10 text-[#0D1830] border-0 text-[10px]">
                    {unreadCount} new
                  </Badge>
                )}
              </div>
              {unreadCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={markAllRead}
                  disabled={loading}
                  className="text-xs text-[#0D1830] hover:text-[#1D4053] h-7 px-2"
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-[400px] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-12 text-center">
                  <Bell className="w-8 h-8 text-[#1D4053]/30 mx-auto mb-3" />
                  <p className="text-sm text-[#72A4BF]">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.notification_id}
                    className={`px-4 py-3 border-b border-[#F0F4F7] hover:bg-[#F7FAFB] cursor-pointer transition-colors ${
                      !notif.read ? 'bg-[#72A4BF]/10' : ''
                    }`}
                    onClick={() => {
                      if (!notif.read) markAsRead(notif.notification_id);
                      if (notif.link) window.location.href = notif.link;
                    }}
                    data-testid={`notification-${notif.notification_id}`}
                  >
                    <div className="flex gap-3">
                      <div className="shrink-0 mt-0.5">
                        {getIcon(notif.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!notif.read ? 'font-medium text-[#0D1830]' : 'text-[#0D1830]'}`}>
                            {notif.title}
                          </p>
                          {!notif.read && (
                            <span className="w-2 h-2 rounded-full bg-[#1D4053] shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-[#446E87] mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-[#72A4BF]/60 mt-1">{formatTime(notif.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-4 py-2 border-t border-[#E2E8ED] bg-[#F7FAFB]">
                <a 
                  href="/settings?tab=notifications" 
                  className="text-xs text-[#0D1830] hover:underline"
                >
                  Manage notification settings
                </a>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
